import React, { useState, useEffect } from "react";
import Button from "../../../../components/ui/Button";
import { Edit, MapPin, Link as LinkIcon, MessageSquare, Check, Camera, Shield, Building, MessageCircle, Share2, Clock, Trash2, Send, Star, User } from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FavoriteButton from "../../../../components/ui/FavoriteButton";
import ReviewModal from "../../../../components/ui/ReviewModal";
import { fetchReviewsByUser } from "../../../../redux/Slices/reviewSlice";
import { calculateUserAverageRating } from "../../../../utils/ratingCalculationUtils";
import { useAppDispatch } from "../../../../redux";
import { useTranslation } from "react-i18next";

export default function ImageSection({
    isEditing,
    handleCoverPhotoUpload,
    profileData,
    editingContact,
    startEditing,
    handleProfilePhotoUpload,
    isCurrentUser = true, // Add prop to determine if current user is viewing their own profile
    userId = null, // Add prop for user ID
    onRemoveProfileImage = null, // Add prop for removing profile image
    canEdit = true, // Add prop to control edit functionality
}) {
    const { t } = useTranslation();
    const { isAuthenticated, user } = useSelector((state: any) => state.auth);
    const { userReviews, userReviewsLoading, userReviewsError } = useSelector((state: any) => state.reviews);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [showReviewModal, setShowReviewModal] = useState(false);

    // StarRatingDisplay component for showing user ratings
    const StarRatingDisplay = ({ rating, count, size = "sm" }: { rating: number; count: number; size?: "sm" | "md" | "lg" }) => {
        const sizeClasses = {
            sm: "h-4 w-4",
            md: "h-5 w-5", 
            lg: "h-6 w-6"
        };

        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;
        const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

        return (
            <div className="flex items-center gap-1">
                <div className="flex">
                    {[...Array(fullStars)].map((_, i) => (
                        <Star key={i} className={`${sizeClasses[size]} text-yellow-400 fill-current`} />
                    ))}
                    {hasHalfStar && (
                        <div className="relative">
                            <Star className={`${sizeClasses[size]} text-gray-300`} />
                            <div className="absolute inset-0 overflow-hidden" style={{ width: "50%" }}>
                                <Star className={`${sizeClasses[size]} text-yellow-400 fill-current`} />
                            </div>
                        </div>
                    )}
                    {[...Array(emptyStars)].map((_, i) => (
                        <Star key={i + fullStars + (hasHalfStar ? 1 : 0)} className={`${sizeClasses[size]} text-gray-300`} />
                    ))}
                </div>
                <span className="text-sm font-medium text-blue-700">
                    {rating.toFixed(1)} ({count} reviews)
                </span>
            </div>
        );
    };

    // Fetch user reviews
    const fetchUserReviews = async () => {
        if (!userId) return;
        try {
            await dispatch(fetchReviewsByUser({
                userId: userId,
                query: {
                    page: 1,
                    limit: 100, // Get all reviews for average calculation
                    sort: [{ orderBy: "createdAt", order: "DESC" }]
                }
            }));
        } catch (error) {
            console.error("Error fetching user reviews:", error);
        }
    };

    // Fetch reviews when component mounts
    useEffect(() => {
        if (userId) {
            fetchUserReviews();
        }
    }, [userId]);
    
    const handleShareProfile = async () => {
        const profileUrl = `${window.location.origin}/client/profile/${userId}`;
        try {
            await navigator.clipboard.writeText(profileUrl);
            // You can add a toast notification here if you have one
            alert(t("imageSection.profileUrlCopied"));
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement("textarea");
            textArea.value = profileUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
            alert(t("imageSection.profileUrlCopied"));
        }
    };

    const handleUnauthenticatedAction = () => {
        // Store the current URL for redirect after login/register
        localStorage.setItem("redirectAfterLogin", window.location.pathname);
        navigate("/auth/register");
    };

    const handleUnauthenticatedContact = () => {
        // Store the current URL for redirect after login/register
        localStorage.setItem("redirectAfterLogin", window.location.pathname);
        navigate("/auth/register");
    };

    return (
        <>
            {/* Mobile & Tablet Layout (up to 1024px) */}
            <div className="lg:hidden">
                <div className="relative">
                    {/* Cover Photo Section */}
                    <div className="relative h-48 sm:h-56 md:h-64">
                        {/* Cover Photo Upload/Remove Overlay */}
                        {isEditing && isCurrentUser && canEdit && (
                            <div className="absolute top-4 right-4 z-10 flex gap-2">
                                <label
                                    htmlFor="cover-image-upload"
                                    className="cursor-pointer"
                                    style={{ pointerEvents: "auto" }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <input
                                        id="cover-image-upload"
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleCoverPhotoUpload}
                                    />
                                    <div className="p-2 bg-white/80 rounded-full hover:bg-white transition-colors">
                                        <Camera className="w-5 h-5 text-black" />
                                    </div>
                                </label>
                                {profileData.personal.cover && onRemoveProfileImage && (
                                    <button
                                        type="button"
                                        onClick={() => onRemoveProfileImage("cover")}
                                        className="p-2 bg-red-600/80 rounded-full text-white hover:bg-red-600 transition-colors"
                                        title="Remove cover photo"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                )}
                            </div>
                        )}

                        <div
                            className="absolute inset-0 bg-cover bg-center"
                            style={{
                                backgroundImage: `url(${profileData.personal.cover})`,
                                backgroundColor: "rgba(0, 0, 0, 0.4)",
                                backgroundBlendMode: "overlay"
                            }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60" />
                    </div>

                    {/* Profile Content Card */}
                    <div className="bg-white px-4 sm:px-6 pb-6 pt-4">
                        {/* Profile Header - Image and Name */}
                        <div className="flex gap-4 items-start mb-6 -mt-16 sm:-mt-20">
                            {/* Profile Image */}
                            <div className="relative flex-shrink-0">
                                <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                                    {profileData.personal.photo && profileData.personal.photo.trim() !== "" ? (
                                        <img
                                            src={profileData.personal.photo}
                                            alt={profileData.personal.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.currentTarget.style.display = "none";
                                                const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                                if (nextElement) {
                                                    nextElement.style.display = "flex";
                                                }
                                            }}
                                        />
                                    ) : null}
                                    <div className={`w-full h-full flex items-center justify-center ${profileData.personal.photo && profileData.personal.photo.trim() !== "" ? "hidden" : "flex"}`}>
                                        <User className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 text-gray-400" />
                                    </div>
                                </div>

                                {/* Profile Image Upload Overlay */}
                                {(isEditing || editingContact) && isCurrentUser && canEdit && (
                                    <div className="absolute bottom-1 right-1 flex gap-1">
                                        <label
                                            htmlFor="profile-image-upload"
                                            className="p-1.5 bg-blue-600 rounded-full text-white hover:bg-blue-700 cursor-pointer shadow-md"
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <Camera className="w-3 h-3 sm:w-4 sm:h-4" />
                                            <input
                                                id="profile-image-upload"
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={handleProfilePhotoUpload}
                                            />
                                        </label>
                                        {profileData.personal.photo && profileData.personal.photo.trim() !== "" && onRemoveProfileImage && (
                                            <button
                                                type="button"
                                                    onClick={() => onRemoveProfileImage("profile")}
                                                    className="p-1.5 bg-red-600 rounded-full text-white hover:bg-red-700 cursor-pointer shadow-md"
                                                    title={t("imageSection.removeProfilePhoto")}
                                                >
                                                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Verification Badge */}
                                {profileData.personal.verified && (
                                    <div className="absolute top-0 right-0 bg-green-500 text-white p-1.5 rounded-full shadow-md">
                                        <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
                                    </div>
                                )}
                            </div>

                            {/* Name and Title */}
                            <div className="flex-1 min-w-0 pt-14 sm:pt-14 md:pt-16">
                                <div className="flex items-start gap-2 mb-2">
                                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 truncate">{profileData.personal.name}</h1>
                                    {profileData.personal.verified && (
                                        <div className="bg-green-500 text-white p-1 rounded-full flex-shrink-0">
                                            <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                                        </div>
                                    )}
                                </div>
                                <p className="text-base sm:text-lg md:text-xl text-gray-600 line-clamp-2">{profileData.personal.title}</p>
                            </div>
                        </div>

                        {/* Metadata Grid */}
                        <div className="mb-6">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-gray-600 text-sm">
                                {/* Location */}
                                <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4 flex-shrink-0" />
                                    <span className="truncate">
                                        {profileData?.personal?.location && profileData?.personal?.location.length > 0
                                            ? profileData?.personal?.location
                                            : t("imageSection.noLocation")}
                                    </span>
                                </div>

                                {/* Timezone */}
                                <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 flex-shrink-0" />
                                    <span className="truncate">
                                        {profileData.personal.timezone || t("imageSection.noTimezone")}
                                    </span>
                                </div>

                                {/* Company */}
                                {profileData.personal.company && profileData.personal.company.name && (
                                    <div className="flex items-center gap-2">
                                        <Building className="w-4 h-4 flex-shrink-0" />
                                        <span className="truncate">{profileData.personal.company.name}</span>
                                    </div>
                                )}

                                {/* Rating */}
                                <div className="flex items-center gap-2">
                                    {userReviewsLoading ? (
                                        <>
                                            <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                                            <span className="text-sm text-gray-500">{t("imageSection.loading")}</span>
                                        </>
                                    ) : userReviews && userReviews.length > 0 ? (
                                        (() => {
                                            const ratingData = calculateUserAverageRating(userReviews, userId);
                                            return (
                                                <button
                                                    onClick={() => setShowReviewModal(true)}
                                                    className="flex items-center gap-1 hover:opacity-80 transition-opacity w-full"
                                                    title={t("imageSection.reviews")}
                                                >
                                                    <StarRatingDisplay 
                                                        rating={ratingData.average} 
                                                        count={ratingData.count} 
                                                        size="sm" 
                                                    />
                                                </button>
                                            );
                                        })()
                                    ) : (
                                        <>
                                            <Star className="w-4 h-4 text-gray-300" />
                                            <span className="text-sm text-gray-500">{t("imageSection.noReviewsYet")}</span>
                                        </>
                                    )}
                                </div>

                                {/* Favorite Button */}
                                <div className="flex items-center gap-2 col-span-2">
                                    <FavoriteButton
                                        targetUserId={userId}
                                        size="sm"
                                        showText={true}
                                        className="p-0 rounded-none bg-transparent shadow-none hover:bg-transparent"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                            {isCurrentUser && canEdit ? (
                                <Button
                                    onClick={startEditing}
                                    variant="outline"
                                    className="w-full bg-white border-gray-300 hover:bg-gray-100 text-black"
                                >
                                    <Edit className="w-4 h-4 mr-2" />
                                    {t("imageSection.editProfile")}
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleUnauthenticatedAction}
                                    variant="outline"
                                    className="w-full bg-white border-gray-300 hover:bg-gray-100 text-black"
                                >
                                    <Send className="w-4 h-4 mr-2" />
                                    {t("imageSection.sendQuote")}
                                </Button>
                            )}

                            <Button
                                onClick={isCurrentUser ? handleShareProfile : handleUnauthenticatedContact}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                {isCurrentUser ? (
                                    <>
                                        <Share2 className="w-4 h-4 mr-2" />
                                        {t("imageSection.share")}
                                    </>
                                ) : (
                                    <>
                                        <MessageCircle className="w-4 h-4 mr-2" />
                                        {t("imageSection.contact")}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Desktop Layout (1024px and above) */}
            <div className="hidden lg:block">
                <div className="relative h-[400px]">
                    {/* Cover Photo */}
                    <div className="relative h-full">
                        {/* Cover Photo Upload/Remove Overlay */}
                        {isEditing && isCurrentUser && canEdit && (
                            <div className="absolute top-4 right-4 z-10 flex gap-2">
                                <label
                                    htmlFor="cover-image-upload-desktop"
                                    className="cursor-pointer"
                                    style={{ pointerEvents: "auto" }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <input
                                        id="cover-image-upload-desktop"
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleCoverPhotoUpload}
                                    />
                                    <div className="p-2 bg-white/80 rounded-full hover:bg-white transition-colors">
                                        <Camera className="w-5 h-5 text-black" />
                                    </div>
                                </label>
                                {profileData.personal.cover && onRemoveProfileImage && (
                                    <button
                                        type="button"
                                        onClick={() => onRemoveProfileImage("cover")}
                                        className="p-2 bg-red-600/80 rounded-full text-white hover:bg-red-600 transition-colors"
                                        title="Remove cover photo"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                )}
                            </div>
                        )}

                        <div
                            className="absolute inset-0 bg-cover bg-center"
                            style={{
                                backgroundImage: `url(${profileData.personal.cover})`,
                                backgroundColor: "rgba(0, 0, 0, 0.4)",
                                backgroundBlendMode: "overlay"
                            }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80" />
                    </div>

                    {/* Desktop Profile Content */}
                    <div className="absolute inset-x-0 bottom-0 px-4 pb-6 z-20">
                        <div className="max-w-6xl mx-auto">
                            {/* Desktop Layout - Horizontal */}
                            <div className="flex items-end gap-6 pt-6">
                                {/* Profile Image */}
                                <div className="relative">
                                    <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                                        {profileData.personal.photo && profileData.personal.photo.trim() !== "" ? (
                                            <img
                                                src={profileData.personal.photo}
                                                alt={profileData.personal.name}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = "none";
                                                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                                    if (nextElement) {
                                                        nextElement.style.display = "flex";
                                                    }
                                                }}
                                            />
                                        ) : null}
                                        <div className={`w-full h-full flex items-center justify-center ${profileData.personal.photo && profileData.personal.photo.trim() !== "" ? "hidden" : "flex"}`}>
                                            <User className="w-16 h-16 text-gray-400" />
                                        </div>
                                    </div>

                                    {/* Profile Image Upload Overlay */}
                                    {(isEditing || editingContact) && isCurrentUser && canEdit && (
                                        <div className="absolute bottom-2 right-2 flex gap-1">
                                            <label
                                                htmlFor="profile-image-upload-desktop"
                                                className="p-2 bg-blue-600 rounded-full text-white hover:bg-blue-700 cursor-pointer"
                                                onClick={e => e.stopPropagation()}
                                            >
                                                <Camera className="w-4 h-4" />
                                                <input
                                                    id="profile-image-upload-desktop"
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={handleProfilePhotoUpload}
                                                />
                                            </label>
                                            {profileData.personal.photo && profileData.personal.photo.trim() !== "" && onRemoveProfileImage && (
                                                <button
                                                    type="button"
                                                    onClick={() => onRemoveProfileImage("profile")}
                                                    className="p-2 bg-red-600 rounded-full text-white hover:bg-red-700 cursor-pointer"
                                                    title={t("imageSection.removeProfilePhoto")}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {/* Verification Badge */}
                                    {profileData.personal.verified && (
                                        <div className="absolute -bottom-2 -right-2 bg-green-500 text-white p-1 rounded-full">
                                            <Shield className="w-4 h-4" />
                                        </div>
                                    )}
                                </div>

                                {/* Profile Info - Desktop */}
                                <div className="text-white flex-1">
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <h1 className="text-3xl font-bold">{profileData.personal.name}</h1>
                                            {profileData.personal.verified && (
                                                <div className="bg-green-500 text-white p-1 rounded-full">
                                                    <Check className="w-4 h-4" />
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-xl text-gray-200">{profileData.personal.title}</p>
                                        <div className="flex items-center gap-4 text-gray-300 text-sm">
                                            <div className="flex items-center gap-1">
                                                <MapPin className="w-4 h-4" />
                                                {profileData?.personal?.location && profileData?.personal?.location.length > 0
                                                    ? profileData?.personal?.location
                                                    : t("imageSection.noLocation")}
                                            </div>
                                            {profileData.personal.company && profileData.personal.company.name && (
                                                <>
                                                    <div>•</div>
                                                    <div className="flex items-center gap-1">
                                                        <Building className="w-4 h-4" />
                                                        {profileData.personal.company.name}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        {/* Timezone */}
                                        {profileData.personal.timezone && (
                                            <div className="flex items-center gap-1 text-gray-300 text-sm">
                                                <Clock className="w-4 h-4" />
                                                {t("imageSection.timezone")} {profileData.personal.timezone}
                                            </div>
                                        )}
                                        {!profileData.personal.timezone && (
                                            <div className="flex items-center gap-1 text-gray-300 text-sm">
                                                <Clock className="w-4 h-4" />
                                                {t("imageSection.noTimezone")}
                                            </div>
                                        )}
                                        {/* Rating Display */}
                                        <div className="flex items-center gap-1 text-gray-300 text-sm">
                                            {userReviewsLoading ? (
                                                <div className="flex items-center gap-1">
                                                    <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                                                    <span className="text-sm text-gray-300">{t("imageSection.loading")}</span>
                                                </div>
                                            ) : userReviews && userReviews.length > 0 ? (
                                                (() => {
                                                    const ratingData = calculateUserAverageRating(userReviews, userId);
                                                    return (
                                                        <button
                                                            onClick={() => setShowReviewModal(true)}
                                                            className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                                                            title={t("imageSection.reviews")}
                                                        >
                                                            <StarRatingDisplay 
                                                                rating={ratingData.average} 
                                                                count={ratingData.count} 
                                                                size="sm" 
                                                            />
                                                        </button>
                                                    );
                                                })()
                                            ) : (
                                                <div className="flex items-center gap-1">
                                                    <Star className="w-4 h-4 text-gray-300" />
                                                    <span className="text-sm text-gray-300">{t("imageSection.noReviewsYet")}</span>
                                                </div>
                                            )}
                                        </div>
                                        {/* Favorite Button */}
                                        <div className="flex items-center gap-1 text-gray-300 text-sm">
                                            <FavoriteButton
                                                targetUserId={userId}
                                                size="sm"
                                                showText={true}
                                                className="p-0 rounded-none bg-transparent shadow-none hover:bg-transparent"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Desktop Action Buttons */}
                            <div className="flex flex-row gap-2 mt-4">
                                {isCurrentUser && canEdit ? (
                                    <Button
                                        onClick={startEditing}
                                        variant="outline"
                                        className="w-auto ml-auto bg-white border-gray hover:bg-gray-100 text-black"
                                    >
                                        <Edit className="w-4 h-4 mr-2" />
                                        {t("imageSection.editProfile")}
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleUnauthenticatedAction}
                                        variant="outline"
                                        className="w-auto ml-auto bg-white border-gray hover:bg-gray-100 text-black"
                                    >
                                        <Send className="w-4 h-4 mr-2" />
                                        {t("imageSection.sendQuote")}
                                    </Button>
                                )}

                                <Button
                                    onClick={isCurrentUser ? handleShareProfile : handleUnauthenticatedContact}
                                    className="w-auto bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                    {isCurrentUser ? (
                                        <>
                                            <Share2 className="w-4 h-4 mr-2" />
                                            {t("imageSection.share")}
                                        </>
                                    ) : (
                                        <>
                                            <MessageCircle className="w-4 h-4 mr-2" />
                                            {t("imageSection.contact")}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Review Modal */}
            <ReviewModal
                isOpen={showReviewModal}
                onClose={() => setShowReviewModal(false)}
                professional={profileData.personal ? {
                    id: userId,
                    name: profileData.personal.name || `${profileData.personal.firstName || ""} ${profileData.personal.lastName || ""}`.trim(),
                    firstName: profileData.personal.firstName,
                    lastName: profileData.personal.lastName,
                    title: profileData.personal.title,
                    photo: profileData.personal.photo ? { path: profileData.personal.photo } : undefined,
                    averageRating: userReviews && userReviews.length > 0 ? calculateUserAverageRating(userReviews, userId).average : 0,
                    reviewsCount: userReviews ? userReviews.length : 0,
                    reviewsList: userReviews || []
                } : null}
            />
        </>
    );
}
